import type { ReactNode } from "react";
import { useIntl, defineMessages, type IntlShape } from "react-intl";
import classnames from "classnames";
import { get } from "react-hook-form"; // eslint-disable-line no-restricted-imports

import { useA11y } from "common/accessibility";

import type { FieldError, UseFormReturn } from "..";
import Styles from "./index.module.scss";

// We want a11y components!
export type AriaInvalidPropValue = "true" | "false" | boolean;
export type AriaRequired<Orig> = Omit<Orig, "aria-invalid"> & {
  "aria-invalid": AriaInvalidPropValue;
};
type FieldErrorMessageProps = {
  inputName: string;
  className?: string;
  message: ReactNode;
};
type FormattedFieldErrorProps = Omit<FieldErrorMessageProps, "message"> & {
  error?: FieldError;
};

const VALIDATION_MESSAGES = defineMessages({
  default: {
    id: "d4b48a5f-4493-408e-851d-aff234c47d58",
    defaultMessage: "This doesn't look right",
  },
  required: {
    id: "cd701f9c-5c2b-4005-9611-2e2368270726",
    defaultMessage: "This is required",
  },
  atLeastOne: {
    id: "be4acf68-39cd-41dc-b2a9-b257001134f1",
    defaultMessage: "At least one of these is required",
  },
  emailPattern: {
    id: "d87ba5b2-b45d-45a1-8290-fb422293e987",
    defaultMessage: "This email doesn't look right",
  },
});

export function defaultRequiredMessage(intl: IntlShape) {
  return intl.formatMessage(VALIDATION_MESSAGES.required);
}

export function atLeastOneRequiredMessage(intl: IntlShape) {
  return intl.formatMessage(VALIDATION_MESSAGES.atLeastOne);
}

export function emailPatternValidation(intl: IntlShape) {
  return {
    message: intl.formatMessage(VALIDATION_MESSAGES.emailPattern),
    // WARNING: This is purposefully simple. You cannot hope to validate emails correctly by characters alone.
    // Think deeply about expected value before making this more complex. Make sure your flow allows the user
    // to correct mistakes in mistyped emails (resend actions, edit email, etc) that _will_ happen regardless
    // of how fancy this regex is.
    value: /^.+@.+\..+$/,
  };
}

export function isAriaInvalid(isInvalid: unknown): "true" | "false" {
  return Boolean(isInvalid).toString() as "true" | "false";
}

/** Use this function to add describedby attrs to a field with FieldErrorMessage */
export function useAriaErrorDescribedId(inputProps: {
  name?: string;
  "aria-describedby"?: string;
}): string | undefined {
  const { useLabelledOrDescribedBy } = useA11y();
  return (
    classnames(useLabelledOrDescribedBy(inputProps.name), inputProps["aria-describedby"]) ||
    undefined
  );
}

export function FieldErrorMessage({ message, className, inputName }: FieldErrorMessageProps) {
  // This is always on display so as to not make the page jump when the message arrives.
  // All validation errors should be aria-describedby the associated input
  const { useRegisteredId } = useA11y();
  const hasMessage = Boolean(message);
  const fieldErrorId = useRegisteredId(hasMessage && inputName);
  return (
    <span id={fieldErrorId} className={classnames(Styles.error, className)} role="alert">
      {message}
    </span>
  );
}

export function FormattedFieldError({ error, ...rest }: FormattedFieldErrorProps) {
  const intl = useIntl();
  return (
    <FieldErrorMessage
      {...rest}
      message={error ? error.message || intl.formatMessage(VALIDATION_MESSAGES.default) : null}
    />
  );
}

export function getNestedFormErrors<
  FormValues extends Record<string, unknown>,
  NestedShape extends Record<string, unknown>,
>(form: UseFormReturn<FormValues>, path: string): { [key in keyof NestedShape]?: FieldError } {
  return get(form.formState.errors, path) || {};
}
